import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Form, Modal, Badge, Tabs, Tab } from 'react-bootstrap';
import { FaRobot, FaArrowRight, FaCodeBranch, FaPlus, FaEdit, FaTrash, FaSync, FaVial, FaHistory } from 'react-icons/fa';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import { cacheService, CACHE_KEYS, CACHE_TTL } from '../utils/cacheService';
import './AgentsList.css'

const AgentsList = () => {
  const navigate = useNavigate();
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(null);
  const [cacheStatus, setCacheStatus] = useState({ fromCache: false, timestamp: null });
  
  // Agent edit/add modal state
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Initial form state
  const initialFormState = {
    agentName: '',
    agentDescription: '',
    repoName: '',
    repoAccount: 'materassi',
    awsRegion: 'us-west-2',
    awsAccountNumber: '851725399969',
    awsRoleForGithub: 'arn:aws:iam::851725399969:role/AgentAWSDeveloperGithub',
    githubTokenSecretName: 'GitHubToken',
    commissioned: true,
    deployedInDev: false,
    deployedInProd: false,
    // New fields
    frontend_github_account: '',
    frontend_github_repository: '',
    frontend_website_domain_dev: '',
    frontend_website_s3_bucket_dev: '',
    frontend_website_domain_prod: '',
    frontend_website_s3_bucket_prod: ''
  };
  
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  useEffect(() => {
    loadAgents();
  }, []);
  
  // Add class to body when modal is open for blur effect
  useEffect(() => {
    if (showModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showModal]);
  
  const loadAgents = async (forceRefresh = false) => {
    try {
      console.log('Loading agents from API...');
      setLoading(true);
      setError(null);
      
      // Check cache first unless force refresh
      if (!forceRefresh) {
        const cachedData = cacheService.get(CACHE_KEYS.AGENTS_LIST);
        
        if (cachedData) {
          console.log('Loading agents from cache');
          setAgents(cachedData.agents);
          setCacheStatus({ fromCache: true, timestamp: cachedData.timestamp });
          setLoading(false);
          
          // Fetch fresh data in background
          loadFreshAgents();
          return;
        }
      } else {
        // Clear cache if forcing refresh
        cacheService.remove(CACHE_KEYS.AGENTS_LIST);
      }
      
      // Fetch fresh data
      await loadFreshAgents();
      
    } catch (err) {
      console.error('Error in loadAgents function:', err);
      setError(`Failed to load agents: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const loadFreshAgents = async () => {
    try {
      const response = await api.getAgents();
      console.log('API response received:', response ? 'yes' : 'no');
      
      if (response && response.agents && Array.isArray(response.agents)) {
        console.log('Processing response with agents array');
        
        // Map the agents to the expected format
        const mappedAgents = response.agents.map(agent => ({
          id: agent.uniqueIdentifier,
          name: agent.agentName || agent.repoName,
          description: agent.agentDescription || `${agent.repoName} agent`,
          status: agent.commissioned ? 'active' : 'inactive',
          deployedInDev: agent.deployedInDev,
          deployedInProd: agent.deployedInProd,
          repoName: agent.repoName,
          repoAccount: agent.repoAccount,
          awsRegion: agent.awsRegion,
          awsAccountNumber: agent.awsAccountNumber,
          awsRoleForGithub: agent.awsRoleForGithub,
          githubTokenSecretName: agent.githubTokenSecretName,
          commissioned: agent.commissioned,
          config: agent.config || {},
          // Store all original data for editing
          originalData: agent
        }));
        
        setAgents(mappedAgents);
        
        // Cache the data
        const cacheData = {
          agents: mappedAgents,
          timestamp: Date.now()
        };
        cacheService.set(CACHE_KEYS.AGENTS_LIST, cacheData, CACHE_TTL.MEDIUM);
        setCacheStatus({ fromCache: false, timestamp: Date.now() });
        
      } else if (response && response.data && Array.isArray(response.data)) {
        setAgents(response.data);
        
        const cacheData = {
          agents: response.data,
          timestamp: Date.now()
        };
        cacheService.set(CACHE_KEYS.AGENTS_LIST, cacheData, CACHE_TTL.MEDIUM);
        setCacheStatus({ fromCache: false, timestamp: Date.now() });
      } else {
        console.error('Unexpected response format:', response);
        throw new Error('Unexpected response format from API');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading fresh agents:', err);
      // Only set error if we don't have cached data
      if (!cacheStatus.fromCache) {
        setError(`Failed to load agents: ${err.message || 'Unknown error'}`);
      }
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setUpdateSuccess('Refreshing agents...');
    loadAgents(true);
    setTimeout(() => setUpdateSuccess(null), 3000);
  };
  
  const handleAgentSelect = (agent) => {
    // Navigate to the agent detail page
    navigate(`/parent/${agent.name || agent.id}`);
  };

  const handleDevelop = (agent, e) => {
    e.stopPropagation();
    navigate(`/parent/${agent.name || agent.id}`);
  };

  const handleTest = (agent, e) => {
    e.stopPropagation();
    navigate(`/agents/${agent.id}/testing`);
  };

  const handleLogs = (agent, e) => {
    e.stopPropagation();
    navigate(`/agents/${agent.id}/logs`);
  };

  // New function to handle adding a new agent
  const handleAddAgent = () => {
    setEditMode(false);
    setCurrentAgent(null);
    setFormData(initialFormState);
    setFormErrors({});
    setActiveTab('basic');
    setShowModal(true);
  };

  // New function to handle editing an existing agent
  const handleEditAgent = (agent, e) => {
    e.stopPropagation(); // Prevent bubbling to the card click handler
    
    setEditMode(true);
    setCurrentAgent(agent);
    
    // Prepare form data from agent
    const data = {
      ...initialFormState,
      agentName: agent.name || '',
      agentDescription: agent.description || '',
      repoName: agent.repoName || '',
      repoAccount: agent.repoAccount || 'materassi',
      awsRegion: agent.awsRegion || 'us-west-2',
      awsAccountNumber: agent.awsAccountNumber || '851725399969',
      awsRoleForGithub: agent.awsRoleForGithub || 'arn:aws:iam::851725399969:role/AgentAWSDeveloperGithub',
      githubTokenSecretName: agent.githubTokenSecretName || 'GitHubToken',
      commissioned: agent.commissioned !== undefined ? agent.commissioned : true,
      deployedInDev: agent.deployedInDev || false,
      deployedInProd: agent.deployedInProd || false,
      uniqueIdentifier: agent.id
    };

    // If original data has more fields, use them
    if (agent.originalData) {
      const orig = agent.originalData;
      data.frontend_github_account = orig.frontend_github_account || '';
      data.frontend_github_repository = orig.frontend_github_repository || '';
      data.frontend_website_domain_dev = orig.frontend_website_domain_dev || '';
      data.frontend_website_s3_bucket_dev = orig.frontend_website_s3_bucket_dev || '';
      data.frontend_website_domain_prod = orig.frontend_website_domain_prod || '';
      data.frontend_website_s3_bucket_prod = orig.frontend_website_s3_bucket_prod || '';
    }

    setFormData(data);
    setFormErrors({});
    setActiveTab('basic');
    setShowModal(true);
  };

  // New function to handle deleting an agent
  const handleDeleteAgent = async (agent, e) => {
    e.stopPropagation(); // Prevent bubbling to the card click handler
    
    if (window.confirm(`Are you sure you want to delete the agent "${agent.name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        setError(null);
        
        const payload = {
          operation: "delete",
          uniqueIdentifier: agent.id
        };
        
        const response = await api.makeAuthenticatedRequest('PUT', '/table/agents', payload);
        
        if (response && response.success) {
          // Remove the agent from the list
          setAgents(agents.filter(a => a.id !== agent.id));
          setUpdateSuccess(`Agent "${agent.name}" deleted successfully.`);
          
          // Clear cache to force refresh on next load
          cacheService.remove(CACHE_KEYS.AGENTS_LIST);
        } else {
          throw new Error(response?.message || 'Failed to delete agent');
        }
      } catch (err) {
        console.error('Error deleting agent:', err);
        setError(`Failed to delete agent: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.agentName) errors.agentName = 'Agent name is required';
    if (!formData.repoName) errors.repoName = 'Repository name is required';
    if (!formData.repoAccount) errors.repoAccount = 'Repository account is required';
    if (!formData.awsRegion) errors.awsRegion = 'AWS region is required';
    if (!formData.awsRoleForGithub) errors.awsRoleForGithub = 'AWS role for GitHub is required';
    if (!formData.githubTokenSecretName) errors.githubTokenSecretName = 'GitHub token secret name is required';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      setActiveTab('basic'); // Switch to basic tab where required fields are
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Prepare the payload
      let payload = {
        data: {
          deployedInDev: formData.deployedInDev,
          repoName: formData.repoName,
          awsAccountNumber: formData.awsAccountNumber,
          commissioned: formData.commissioned,
          githubTokenSecretName: formData.githubTokenSecretName,
          repoAccount: formData.repoAccount,
          agentDescription: formData.agentDescription,
          deployedInProd: formData.deployedInProd,
          awsRegion: formData.awsRegion,
          awsRoleForGithub: formData.awsRoleForGithub,
          agentName: formData.agentName,
          // New extended fields
          frontend_github_account: formData.frontend_github_account,
          frontend_github_repository: formData.frontend_github_repository,
          frontend_website_domain_dev: formData.frontend_website_domain_dev,
          frontend_website_s3_bucket_dev: formData.frontend_website_s3_bucket_dev,
          frontend_website_domain_prod: formData.frontend_website_domain_prod,
          frontend_website_s3_bucket_prod: formData.frontend_website_s3_bucket_prod
        }
      };
      
      if (editMode) {
        // Update existing agent
        payload.operation = "update";
        payload.data.uniqueIdentifier = formData.uniqueIdentifier;
      } else {
        // Create new agent
        payload.operation = "create";
      }
      
      const response = await api.makeAuthenticatedRequest('PUT', '/table/agents', payload);
      
      if (response && response.success) {
        // Close the modal
        setShowModal(false);
        
        // Show success message
        setUpdateSuccess(editMode 
          ? `Agent "${formData.agentName}" updated successfully.` 
          : `Agent "${formData.agentName}" created successfully.`);
        
        // Clear cache and reload agents
        cacheService.remove(CACHE_KEYS.AGENTS_LIST);
        loadAgents(true);
      } else {
        throw new Error(response?.message || 'Failed to save agent');
      }
    } catch (err) {
      console.error('Error saving agent:', err);
      setError(`Failed to save agent: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="py-4 agents-list-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Develop & Enhance Agents</h1>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            onClick={handleRefresh}
            disabled={loading}
          >
            <FaSync className={`me-2 ${loading ? 'fa-spin' : ''}`} /> Refresh
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddAgent}
          >
            <FaPlus className="me-2" /> Add New Agent
          </Button>
        </div>
      </div>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      {updateSuccess && (
        <Alert variant="success" className="mb-4">
          {updateSuccess}
        </Alert>
      )}
      
      {loading && agents.length === 0 ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner />
          <p className="ms-3 my-0">Loading agents...</p>
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center p-5 bg-light rounded">
          <FaCodeBranch size={48} className="text-muted mb-3" />
          <p className="mb-3">
            No agents available for development.
          </p>
          <Button variant="primary" onClick={handleAddAgent}>
            <FaPlus className="me-2" /> Add New Agent
          </Button>
        </div>
      ) : (
        <Row>
          {agents.map(agent => (
            <Col key={agent.id} xs={12} sm={6} lg={4} className="mb-4">
              <Card className="agent-card" onClick={() => handleAgentSelect(agent)}>
                <Card.Body className="d-flex flex-column">
                  {/* Compact Action Buttons in Top-Right */}
                  <div className="agent-card-actions">
                    <Button 
                      variant="outline-light" 
                      size="sm" 
                      className="action-button"
                      onClick={(e) => handleDevelop(agent, e)}
                      title="Develop"
                    >
                      Develop
                    </Button>
                    <Button 
                      variant="outline-light" 
                      size="sm"
                      className="action-button"
                      onClick={(e) => handleLogs(agent, e)}
                      title="Logs"
                    >
                      Logs
                    </Button>
                    <Button 
                      variant="outline-light" 
                      size="sm"
                      className="action-button"
                      onClick={(e) => handleTest(agent, e)}
                      title="Test"
                    >
                      Test
                    </Button>
                  </div>

                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Card.Title as="h4" className="agent-card-title">{agent.name}</Card.Title>
                  </div>

                  <Card.Text className="agent-card-description">
                    <div style={{ width: '100%', textAlign: 'justify' }}>
                      {agent.description || 'No description available'}
                    </div>
                  </Card.Text>
                  
                  <Button 
                    variant="primary" 
                    onClick={() => handleAgentSelect(agent)}
                    className="mt-auto align-self-start"
                  >
                    Develop <FaArrowRight className="ms-1" />
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Agent Edit/Add Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        dialogClassName="modal-dialog-centered"
        contentClassName="modal-content-custom"
        className="agent-modal"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? 'Edit Agent' : 'Add New Agent'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4"
            >
              <Tab eventKey="basic" title="Basic Info">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Agent Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="agentName"
                        value={formData.agentName}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.agentName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.agentName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Repository Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="repoName"
                        value={formData.repoName}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.repoName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.repoName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="agentDescription"
                    value={formData.agentDescription}
                    onChange={handleInputChange}
                  />
                </Form.Group>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>GitHub Account <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="repoAccount"
                        value={formData.repoAccount}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.repoAccount}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.repoAccount}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>GitHub Token Secret Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="githubTokenSecretName"
                        value={formData.githubTokenSecretName}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.githubTokenSecretName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.githubTokenSecretName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Commissioned"
                        name="commissioned"
                        checked={formData.commissioned}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Deployed in Development"
                        name="deployedInDev"
                        checked={formData.deployedInDev}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Deployed in Production"
                        name="deployedInProd"
                        checked={formData.deployedInProd}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>
              
              <Tab eventKey="aws" title="AWS Configuration">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>AWS Region <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="awsRegion"
                        value={formData.awsRegion}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.awsRegion}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.awsRegion}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>AWS Account Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="awsAccountNumber"
                        value={formData.awsAccountNumber}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3">
                  <Form.Label>AWS Role for GitHub <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="awsRoleForGithub"
                    value={formData.awsRoleForGithub}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.awsRoleForGithub}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.awsRoleForGithub}
                  </Form.Control.Feedback>
                </Form.Group>
              </Tab>

              <Tab eventKey="frontend" title="Frontend Configuration">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Frontend GitHub Account</Form.Label>
                      <Form.Control
                        type="text"
                        name="frontend_github_account"
                        value={formData.frontend_github_account}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Frontend GitHub Repository</Form.Label>
                      <Form.Control
                        type="text"
                        name="frontend_github_repository"
                        value={formData.frontend_github_repository}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Website Domain (Dev)</Form.Label>
                      <Form.Control
                        type="text"
                        name="frontend_website_domain_dev"
                        value={formData.frontend_website_domain_dev}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>S3 Bucket (Dev)</Form.Label>
                      <Form.Control
                        type="text"
                        name="frontend_website_s3_bucket_dev"
                        value={formData.frontend_website_s3_bucket_dev}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Website Domain (Prod)</Form.Label>
                      <Form.Control
                        type="text"
                        name="frontend_website_domain_prod"
                        value={formData.frontend_website_domain_prod}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>S3 Bucket (Prod)</Form.Label>
                      <Form.Control
                        type="text"
                        name="frontend_website_s3_bucket_prod"
                        value={formData.frontend_website_s3_bucket_prod}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              editMode ? 'Update Agent' : 'Create Agent'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add opacity overlay when modal is open */}
      {showModal && (
        <div 
          className="modal-backdrop-custom" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1040
          }}
        />
      )}
    </Container>
  );
};

export default AgentsList;